import { useEffect } from 'react';

/**
 *
 * @param {string} html
 * @returns {{ total: number, errored: number } | null}
 */
export const parseLegacyImportCounts = (html) => {
  if (!html) return null;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const tables = doc.querySelectorAll('table');
  let tableErrored = 0;
  tables.forEach((table) => {
    if (table.querySelector('li.text-danger')) tableErrored += 1;
  });

  const fullText = doc.body?.textContent || '';
  const failMatch = fullText.match(/(\d+)\s+\S+\s+failed validation/i);
  const topLevelErrored = failMatch ? parseInt(failMatch[1], 10) : 0;

  return { total: tables.length, errored: Math.max(tableErrored, topLevelErrored) };
};

/**
 * Builds the status line shown above the entry list. Mirrors the format
 * used for modern imports in ImportPage so legacy and modern pages read
 * the same:
 *   "X of Y type imported successfully on DATE."  (clean)
 *   "E of Y type failed validation on DATE."      (one or more errored)
 *
 * @returns {string | null}
 */
export const formatLegacyImportStatus = ({ counts, importType, formattedDate }) => {
  if (!counts || counts.total === 0 || !importType) return null;
  const { total, errored } = counts;
  const typeOfData = total === 1 ? importType : `${importType}s`;
  return errored === 0
    ? `${total} of ${total} ${typeOfData} imported successfully on ${formattedDate}.`
    : `${errored} of ${total} ${typeOfData} failed validation on ${formattedDate}.`;
};

export const useLegacyImportEnhancements = ({
  ref, importType, isLegacyImport, html,
}) => {
  useEffect(() => {
    if (!isLegacyImport) return undefined;
    const root = ref.current;
    if (!root) return undefined;

    const tables = root.querySelectorAll('table');
    const cleanups = [];
    const isVisit = importType === 'visit';

    tables.forEach((table) => {
      const headerRow = table.querySelector('tbody > tr:first-child');
      if (!headerRow) return;
      const cells = headerRow.querySelectorAll(':scope > td');
      const host = isVisit && cells.length > 1 ? cells[1] : cells[0];
      host?.classList.add('legacy-chevron-host');

      const expandedRow = table.querySelector(':scope > tbody > tr:nth-child(2)');
      const errorLis = expandedRow
        ? Array.from(expandedRow.querySelectorAll(':scope > td > li.text-danger'))
        : [];
      const hasErrors = errorLis.length > 0;
      table.setAttribute('data-expanded', hasErrors ? 'true' : 'false');

      if (isVisit && cells[0]) {
        const dateNode = [...cells[0].childNodes].find(
          (n) => n.nodeType === Node.TEXT_NODE && /\d{4}-\d{2}-\d{2}/.test(n.nodeValue),
        );
        if (dateNode) {
          dateNode.nodeValue = dateNode.nodeValue.replace(
            /(\d{4})-(\d{2})-(\d{2})/g,
            '$2/$3/$1',
          );
        }
      }

      if (hasErrors && host) {
        const labels = Array.from(expandedRow.querySelectorAll('.panel-body b'))
          .map((el) => ({
            el,
            norm: el.textContent.replace(/[\s:]+/g, ' ').trim().toLowerCase(),
          }))
          .filter((x) => x.norm)
          .sort((a, b) => b.norm.length - a.norm.length);

        const headerErrorList = document.createElement('ul');
        headerErrorList.className = 'legacy-header-errors';

        errorLis.forEach((li) => {
          const errText = li.textContent.replace(/\s+/g, ' ').trim();
          const errNorm = errText.toLowerCase();

          const match = labels.find(({ norm }) => (
            errNorm === norm
            || errNorm.startsWith(`${norm} `)
            || errNorm.startsWith(`${norm}:`)
          ));
          if (match) {
            const fieldDiv = match.el.closest('div');
            if (fieldDiv) fieldDiv.classList.add('legacy-errored-field');
          }

          const newLi = document.createElement('li');
          newLi.textContent = errText;
          headerErrorList.appendChild(newLi);

          li.classList.add('legacy-error-moved');
        });

        host.classList.add('legacy-chevron-host--has-errors');
        host.appendChild(headerErrorList);
      }

      const toggle = () => {
        const next = table.getAttribute('data-expanded') === 'true' ? 'false' : 'true';
        table.setAttribute('data-expanded', next);
      };
      headerRow.addEventListener('click', toggle);
      cleanups.push(() => headerRow.removeEventListener('click', toggle));
    });

    return () => cleanups.forEach((fn) => fn());
  }, [isLegacyImport, html, importType, ref]);
};
